import { useGSAP } from "@gsap/react";
import {
  CalendarDate,
  endOfWeek,
  getLocalTimeZone,
  startOfWeek,
  today,
  type DateValue,
} from "@internationalized/date";
import gsap from "gsap";
import { useRef, useState } from "react";
import {
  Button as AriaButton,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarMonthPicker,
  Radio,
  RadioGroup,
  RangeCalendar,
  type CalendarState,
  type RangeCalendarState,
} from "react-aria-components";

import calendarIcon from "@/assets/figma/date-picker/calendar.svg";
import decadeUnderline from "@/assets/figma/date-picker/decade-underline.svg";
import doubleLeftIcon from "@/assets/figma/date-picker/double-left.svg";
import doubleRightIcon from "@/assets/figma/date-picker/double-right.svg";
import monthUnderline from "@/assets/figma/date-picker/month-underline.svg";
import singleLeftIcon from "@/assets/figma/date-picker/single-left.svg";
import singleRightIcon from "@/assets/figma/date-picker/single-right.svg";
import yearUnderline from "@/assets/figma/date-picker/year-underline.svg";
import { cn } from "@/shared/lib/cn";
import { MerryButton } from "@/shared/ui/MerryButton";
import {
  getMerryDatePickerDecade,
  getMerryDatePickerFigmaNodeId,
  getMerryDatePickerHeight,
  merryDatePickerFigmaNodeIds,
  type MerryDatePickerMode,
  type MerryDatePickerView,
} from "@/shared/ui/merryDatePickerVariants";

gsap.registerPlugin(useGSAP);

type SharedCalendarState = CalendarState | RangeCalendarState;

export interface MerryDateRange {
  end: CalendarDate;
  start: CalendarDate;
}

interface MerryDatePickerBaseProps {
  "aria-label": string;
  "data-testid"?: string;
  autoFocus?: boolean;
  className?: string;
  defaultView?: MerryDatePickerView;
  isDateUnavailable?: (date: DateValue) => boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  maxValue?: CalendarDate | null;
  minValue?: CalendarDate | null;
  /** 快捷“今天/本周”所使用的权威 IANA 时区。 */
  timeZone?: string;
}

interface MerrySingleDatePickerProps extends MerryDatePickerBaseProps {
  defaultValue?: CalendarDate | null;
  mode?: "single";
  onChange?: (value: CalendarDate) => void;
  value?: CalendarDate | null;
}

interface MerryRangeDatePickerProps extends MerryDatePickerBaseProps {
  defaultValue?: MerryDateRange | null;
  mode: "range";
  onChange?: (value: MerryDateRange) => void;
  value?: MerryDateRange | null;
}

export type MerryDatePickerProps =
  | MerryRangeDatePickerProps
  | MerrySingleDatePickerProps;

const viewMotion = {
  content: {
    enter: { duration: 0.18, ease: "power2.out" },
    exit: { duration: 0.14, ease: "power2.in" },
  },
  height: { duration: 0.32, ease: "power3.inOut" },
} as const;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function shiftFocusedDate(
  state: SharedCalendarState,
  view: MerryDatePickerView,
  direction: -1 | 1,
  isLargerStep: boolean,
) {
  if (view === "day") {
    state.setFocusedDate(
      state.focusedDate.add(
        isLargerStep
          ? { years: direction }
          : { months: direction },
      ),
    );
    return;
  }

  const years =
    view === "month"
      ? isLargerStep
        ? 10
        : 1
      : isLargerStep
        ? 100
        : 10;

  state.setFocusedDate(state.focusedDate.add({ years: years * direction }));
}

function getNavigationLabel(
  view: MerryDatePickerView,
  direction: "previous" | "next",
  isLargerStep: boolean,
) {
  const prefix = direction === "previous" ? "向前" : "向后";

  if (view === "day") {
    return `${prefix}${isLargerStep ? "一年" : "一个月"}`;
  }

  if (view === "month") {
    return `${prefix}${isLargerStep ? "十年" : "一年"}`;
  }

  return `${prefix}${isLargerStep ? "一百年" : "十年"}`;
}

function DatePickerNavigationButton({
  direction,
  isLargerStep,
  onPress,
  view,
}: {
  direction: "previous" | "next";
  isLargerStep: boolean;
  onPress: () => void;
  view: MerryDatePickerView;
}) {
  const source =
    direction === "previous"
      ? isLargerStep
        ? doubleLeftIcon
        : singleLeftIcon
      : isLargerStep
        ? doubleRightIcon
        : singleRightIcon;

  return (
    <MerryButton
      aria-label={getNavigationLabel(view, direction, isLargerStep)}
      className="merry-date-picker__navigation-button"
      hierarchy="level0"
      icon={
        <img
          alt=""
          className={cn(
            "merry-date-picker__navigation-icon",
            isLargerStep
              ? "merry-date-picker__navigation-icon--double"
              : "merry-date-picker__navigation-icon--single",
          )}
          src={source}
        />
      }
      onPress={onPress}
      size={34}
      slot={null}
    />
  );
}

function DatePickerHeader({
  onViewChange,
  state,
  view,
}: {
  onViewChange: (view: MerryDatePickerView) => void;
  state: SharedCalendarState;
  view: MerryDatePickerView;
}) {
  const decade = getMerryDatePickerDecade(state.focusedDate.year);

  return (
    <header className="merry-date-picker__header">
      <div className="merry-date-picker__navigation-group">
        <DatePickerNavigationButton
          direction="previous"
          isLargerStep
          onPress={() => shiftFocusedDate(state, view, -1, true)}
          view={view}
        />
        <DatePickerNavigationButton
          direction="previous"
          isLargerStep={false}
          onPress={() => shiftFocusedDate(state, view, -1, false)}
          view={view}
        />
      </div>

      <div className="merry-date-picker__heading">
        {view === "day" ? (
          <>
            <AriaButton
              aria-label="选择年份"
              className="merry-date-picker__heading-button"
              onPress={() => onViewChange("year")}
              slot={null}
            >
              <span>{state.focusedDate.year}年</span>
              <img alt="" src={yearUnderline} />
            </AriaButton>
            <AriaButton
              aria-label="选择月份"
              className="merry-date-picker__heading-button"
              onPress={() => onViewChange("month")}
              slot={null}
            >
              <span>{state.focusedDate.month}月</span>
              <img alt="" src={monthUnderline} />
            </AriaButton>
          </>
        ) : view === "month" ? (
          <AriaButton
            aria-label="选择年份"
            className="merry-date-picker__heading-button"
            onPress={() => onViewChange("year")}
            slot={null}
          >
            <span>{state.focusedDate.year}年</span>
            <img alt="" src={yearUnderline} />
          </AriaButton>
        ) : (
          <div className="merry-date-picker__heading-label">
            <span>
              {decade.start}年 - {decade.end}年
            </span>
            <img alt="" src={decadeUnderline} />
          </div>
        )}
      </div>

      <div className="merry-date-picker__navigation-group">
        <DatePickerNavigationButton
          direction="next"
          isLargerStep={false}
          onPress={() => shiftFocusedDate(state, view, 1, false)}
          view={view}
        />
        <DatePickerNavigationButton
          direction="next"
          isLargerStep
          onPress={() => shiftFocusedDate(state, view, 1, true)}
          view={view}
        />
      </div>
    </header>
  );
}

function DatePickerDayGrid() {
  return (
    <CalendarGrid
      className="merry-date-picker__day-grid"
      weekdayStyle="narrow"
    >
      <CalendarGridHeader>
        {(day) => (
          <CalendarHeaderCell className="merry-date-picker__weekday">
            {day}
          </CalendarHeaderCell>
        )}
      </CalendarGridHeader>
      <CalendarGridBody>
        {(date) => (
          <CalendarCell
            className="merry-date-picker__day-cell"
            date={date}
          >
            {({ formattedDate }) => formattedDate}
          </CalendarCell>
        )}
      </CalendarGridBody>
    </CalendarGrid>
  );
}

function isMonthOutsideBounds(
  state: SharedCalendarState,
  date: CalendarDate,
) {
  const start = date.set({ day: 1 });
  const end = start.add({ months: 1 }).subtract({ days: 1 });

  return Boolean(
    (state.minValue && end.compare(state.minValue) < 0) ||
      (state.maxValue && start.compare(state.maxValue) > 0),
  );
}

function DatePickerMonthGrid({
  onViewChange,
  state,
}: {
  onViewChange: (view: MerryDatePickerView) => void;
  state: SharedCalendarState;
}) {
  return (
    <CalendarMonthPicker format="numeric">
      {({ "aria-label": ariaLabel, items, onChange, value }) => (
        <RadioGroup
          aria-label={ariaLabel}
          className="merry-date-picker__option-grid"
          onChange={(nextValue) => {
            onChange(Number(nextValue));
            onViewChange("day");
          }}
          value={String(value)}
        >
          {items.map((item) => (
            <Radio
              className="merry-date-picker__option"
              isDisabled={isMonthOutsideBounds(state, item.date)}
              key={item.id}
              onPress={() => {
                if (String(value) === String(item.id)) {
                  onViewChange("day");
                }
              }}
              value={String(item.id)}
            >
              {item.date.month} 月
            </Radio>
          ))}
        </RadioGroup>
      )}
    </CalendarMonthPicker>
  );
}

function isYearOutsideBounds(state: SharedCalendarState, year: number) {
  const start = new CalendarDate(year, 1, 1);
  const end = new CalendarDate(year, 12, 31);

  return Boolean(
    (state.minValue && end.compare(state.minValue) < 0) ||
      (state.maxValue && start.compare(state.maxValue) > 0),
  );
}

function DatePickerYearGrid({
  onViewChange,
  state,
}: {
  onViewChange: (view: MerryDatePickerView) => void;
  state: SharedCalendarState;
}) {
  const decade = getMerryDatePickerDecade(state.focusedDate.year);

  return (
    <RadioGroup
      aria-label="年份"
      className="merry-date-picker__option-grid"
      onChange={(nextValue) => {
        const year = Number(nextValue);
        state.setFocusedDate(state.focusedDate.set({ year }));
        onViewChange("month");
      }}
      value={String(state.focusedDate.year)}
    >
      {decade.years.map((year) => (
        <Radio
          className="merry-date-picker__option"
          data-outside-decade={
            year < decade.start || year > decade.end ? "true" : undefined
          }
          isDisabled={isYearOutsideBounds(state, year)}
          key={year}
          onPress={() => {
            if (year === state.focusedDate.year) {
              onViewChange("month");
            }
          }}
          value={String(year)}
        >
          {year}
        </Radio>
      ))}
    </RadioGroup>
  );
}

function DatePickerQuickAction({
  mode,
  onPress,
}: {
  mode: MerryDatePickerMode;
  onPress: () => void;
}) {
  return (
    <MerryButton
      className="merry-date-picker__quick-action"
      hierarchy="level1"
      icon={<img alt="" className="merry-date-picker__calendar-icon" src={calendarIcon} />}
      onPress={onPress}
      size={34}
      slot={null}
    >
      {mode === "range" ? "本周" : "今天"}
    </MerryButton>
  );
}

function DatePickerContent({
  contentRef,
  mode,
  onQuickAction,
  onViewChange,
  state,
  view,
}: {
  contentRef: React.RefObject<HTMLDivElement | null>;
  mode: MerryDatePickerMode;
  onQuickAction: () => void;
  onViewChange: (view: MerryDatePickerView) => void;
  state: SharedCalendarState;
  view: MerryDatePickerView;
}) {
  return (
    <>
      <span aria-hidden="true" className="merry-date-picker__glass" />
      <div className="merry-date-picker__content" ref={contentRef}>
        <DatePickerHeader
          onViewChange={onViewChange}
          state={state}
          view={view}
        />
        {view === "day" ? (
          <DatePickerDayGrid />
        ) : view === "month" ? (
          <DatePickerMonthGrid onViewChange={onViewChange} state={state} />
        ) : (
          <DatePickerYearGrid onViewChange={onViewChange} state={state} />
        )}
        {view === "day" ? (
          <DatePickerQuickAction mode={mode} onPress={onQuickAction} />
        ) : null}
      </div>
      <span aria-hidden="true" className="merry-date-picker__stroke" />
    </>
  );
}

export function MerryDatePicker(props: MerryDatePickerProps) {
  const {
    "aria-label": ariaLabel,
    "data-testid": testId,
    autoFocus,
    className,
    defaultView = "day",
    isDateUnavailable,
    isDisabled,
    isReadOnly,
    maxValue,
    minValue,
    timeZone = getLocalTimeZone(),
  } = props;
  const mode: MerryDatePickerMode = props.mode ?? "single";
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const displayedViewRef = useRef<MerryDatePickerView>(defaultView);
  const previousRenderedViewRef = useRef<MerryDatePickerView | null>(null);
  const [requestedView, setRequestedView] =
    useState<MerryDatePickerView>(defaultView);
  const [displayedView, setDisplayedView] =
    useState<MerryDatePickerView>(defaultView);

  useGSAP(
    () => {
      const root = rootRef.current;
      const content = contentRef.current;

      if (!root || !content) {
        return;
      }

      gsap.killTweensOf([root, content]);

      if (displayedViewRef.current === requestedView) {
        root.dataset.datePickerAnimating = "false";
        root.dataset.datePickerPhase = "idle";
        return;
      }

      const currentHeight = root.getBoundingClientRect().height;
      gsap.set(root, { height: currentHeight });

      if (prefersReducedMotion()) {
        displayedViewRef.current = requestedView;
        previousRenderedViewRef.current = requestedView;
        setDisplayedView(requestedView);
        gsap.set(root, { clearProps: "height,willChange" });
        gsap.set(content, { clearProps: "opacity,willChange" });
        root.dataset.datePickerAnimating = "false";
        root.dataset.datePickerPhase = "idle";
        return;
      }

      root.dataset.datePickerAnimating = "true";
      root.dataset.datePickerPhase = "exiting";
      gsap.set(content, { willChange: "opacity" });
      gsap.to(content, {
        duration: viewMotion.content.exit.duration,
        ease: viewMotion.content.exit.ease,
        opacity: 0,
        overwrite: "auto",
        onComplete: () => {
          displayedViewRef.current = requestedView;
          setDisplayedView(requestedView);
        },
      });
    },
    { dependencies: [requestedView], scope: rootRef },
  );

  useGSAP(
    () => {
      const root = rootRef.current;
      const content = contentRef.current;

      if (!root || !content) {
        return;
      }

      const previousView = previousRenderedViewRef.current;
      const shouldAnimate =
        previousView !== null &&
        previousView !== displayedView &&
        !prefersReducedMotion();

      previousRenderedViewRef.current = displayedView;
      gsap.killTweensOf([root, content]);

      if (!shouldAnimate) {
        gsap.set(root, { clearProps: "height,willChange" });
        gsap.set(content, { clearProps: "opacity,willChange" });
        root.dataset.datePickerAnimating = "false";
        root.dataset.datePickerPhase = "idle";
        return;
      }

      const finishAnimation = () => {
        root.dataset.datePickerAnimating = "false";
        root.dataset.datePickerPhase = "idle";
        gsap.set(root, { clearProps: "height,willChange" });
        gsap.set(content, { clearProps: "opacity,willChange" });
      };

      root.dataset.datePickerAnimating = "true";
      root.dataset.datePickerPhase = "entering";
      gsap.set(root, { willChange: "height" });
      gsap.set(content, { opacity: 0, willChange: "opacity" });

      const timeline = gsap.timeline({
        onComplete: finishAnimation,
        onInterrupt: finishAnimation,
      });

      timeline.to(
        root,
        {
          duration: viewMotion.height.duration,
          ease: viewMotion.height.ease,
          height: getMerryDatePickerHeight(displayedView),
        },
        0,
      );
      timeline.to(
        content,
        {
          duration: viewMotion.content.enter.duration,
          ease: viewMotion.content.enter.ease,
          opacity: 1,
        },
        viewMotion.content.exit.duration,
      );
    },
    { dependencies: [displayedView], scope: rootRef },
  );

  const changeView = (nextView: MerryDatePickerView) => {
    if (nextView !== requestedView) {
      setRequestedView(nextView);
    }
  };

  const commonCalendarProps = {
    "aria-label": ariaLabel,
    autoFocus,
    className: cn("merry-date-picker", className),
    "data-date-picker-animating": "false",
    "data-date-picker-mode": mode,
    "data-date-picker-motion": "product-gsap",
    "data-date-picker-phase": "idle",
    "data-date-picker-view": displayedView,
    "data-figma-component-id": merryDatePickerFigmaNodeIds.componentSet,
    "data-figma-node-id": getMerryDatePickerFigmaNodeId(mode, displayedView),
    "data-testid": testId,
    firstDayOfWeek: "mon" as const,
    isDateUnavailable,
    isDisabled,
    isReadOnly,
    maxValue,
    minValue,
    ref: rootRef,
    selectionAlignment: "start" as const,
    visibleDuration: { months: 1 },
    weeksInMonth: 6,
  };

  if (props.mode === "range") {
    return (
      <RangeCalendar
        {...commonCalendarProps}
        defaultValue={props.defaultValue}
        onChange={props.onChange}
        value={props.value}
      >
        {({ state }) => (
          <DatePickerContent
            contentRef={contentRef}
            mode="range"
            onQuickAction={() => {
              const currentDate = today(timeZone);
              const start = startOfWeek(currentDate, "zh-CN", "mon");
              const end = endOfWeek(currentDate, "zh-CN", "mon");
              state.setFocusedDate(start);
              state.setValue({ end, start });
            }}
            onViewChange={changeView}
            state={state}
            view={displayedView}
          />
        )}
      </RangeCalendar>
    );
  }

  return (
    <Calendar
      {...commonCalendarProps}
      defaultValue={props.defaultValue}
      onChange={props.onChange}
      value={props.value}
    >
      {({ state }) => (
        <DatePickerContent
          contentRef={contentRef}
          mode="single"
          onQuickAction={() => {
            const currentDate = today(timeZone);
            state.setFocusedDate(currentDate);
            state.selectDate(currentDate);
          }}
          onViewChange={changeView}
          state={state}
          view={displayedView}
        />
      )}
    </Calendar>
  );
}
